import { Avatar, Box, Button, Divider, Stack, Typography } from '@mui/material'
import { forwardRef, useRef } from 'react'
import { Icons } from 'utoniq-ui'

import {
  CommentsConnectionFragment,
  Maybe,
  RepliesConnectionFragment,
  User,
} from '@/clients/utoniq-core/schema'
import { BrowserLink } from '@/components/lib/BrowserLink'
import { ReplyBoxSkeleton } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { styles } from '@/components/styles'
import { useTranslation } from '@/core'
import { CellFrame } from '@/core/architecture'
import { getHelpUrl } from '@/lib/helpUrl'

import { ReplyBox, ReplyBoxProps } from './../../reply'

export type CommentListProps = {
  type?: ReplyBoxProps['type']
  typeId?: ReplyBoxProps['typeId']
  comments: Maybe<CommentsConnectionFragment | RepliesConnectionFragment>
  user: Maybe<Pick<User, 'id' | 'formattedAccountId' | 'avatar'>>
  loading: boolean
  onToggleButtonComment?: () => void
  hasNextPage: boolean
  commentCount?: number
  showReaction?: boolean
  hiddenHeader?: boolean
  hiddenNotice?: boolean
  hiddenReply?: boolean
  onFetchMore: () => Promise<void>
  onDeleteComment: ReplyBoxProps['onDeleteComment']
  onReportUser: ReplyBoxProps['onReportUser']
  onBlockUser: ReplyBoxProps['onBlockUser']
  onLikeOrUnlikeComment?: ReplyBoxProps['onLikeOrUnlikeComment']
  onReplyToReply: ReplyBoxProps['onReplyToReply']
  onClickReply: ReplyBoxProps['onClickReply']
  rootCollectionId?: string
  unreadIndex?: number
  hiddenReadMore?: boolean
  onHiddenReadMore?: () => void
  inverted?: boolean
}
export const CommentList = forwardRef<HTMLDivElement, CommentListProps>(
  (
    {
      type,
      typeId,
      user,
      comments,
      loading,
      onToggleButtonComment,
      showReaction,
      hiddenHeader,
      hiddenNotice = true,
      hiddenReply,
      hasNextPage,
      onFetchMore,
      onDeleteComment,
      onReportUser,
      onBlockUser,
      onLikeOrUnlikeComment,
      onReplyToReply,
      onClickReply,
      commentCount = 0,
      rootCollectionId,
      unreadIndex,
      hiddenReadMore,
      onHiddenReadMore,
      inverted,
    },
    ref
  ) => {
    const { t } = useTranslation('postDetail')
    const scrollBottomRef = useRef<HTMLDivElement>(null)

    return (
      <Box ref={ref} sx={{ px: 5 }}>
        {!hiddenHeader && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ py: 4 }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h4">{t('comment')}</Typography>
              {!!comments?.edges?.length && (
                <Typography variant="h6" color="text.hint">
                  {commentCount ?? comments?.edges?.length}
                </Typography>
              )}
            </Stack>
            {/**
             * @todo
             * ポストのコメントフィルター実装は後工程にて実装
             * https://app.clickup.com/t/908127/UT-4187
             *
             * <IconButton.Slider size="medium" sx={{ mr: -2 }} />
             */}
          </Stack>
        )}
        {!hiddenNotice && <NoticeTextCell />}
        {onToggleButtonComment && (
          <Stack sx={{ pb: 5 }}>
            <Button
              variant="text"
              fullWidth
              sx={{ borderRadius: 0, px: 0, py: 3 }}
              onClick={onToggleButtonComment}
            >
              <Stack
                flex={1}
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="flex-start"
              >
                <Avatar
                  src={user?.avatar?.smallUri ?? ''}
                  sx={{ width: 28, height: 28 }}
                />
                <Typography variant="body2" color="text.disabled">
                  {t('sendComment')}
                </Typography>
              </Stack>
            </Button>
            <Divider />
          </Stack>
        )}
        <ScrollFetcher
          inverted={inverted}
          componentProps={{
            spacing: 5,
          }}
          nodes={comments?.edges.map(({ node }, index) => (
            <ReplyBox
              index={index}
              type={type}
              typeId={typeId}
              key={node.id}
              comment={node}
              divider
              showReaction={showReaction}
              userId={user?.id ?? ''}
              onDeleteComment={onDeleteComment}
              onReportUser={onReportUser}
              onBlockUser={onBlockUser}
              onLikeOrUnlikeComment={onLikeOrUnlikeComment}
              rootCollectionId={rootCollectionId}
              onReplyToReply={onReplyToReply}
              onClickReply={onClickReply}
              hiddenReply={hiddenReply}
              unreadIndex={unreadIndex}
            />
          ))}
          emptyMessage={t('emptyReplyMessage')}
          loading={loading}
          hasNextPage={hasNextPage}
          onFetchMore={onFetchMore}
          loadingComponent={
            <Stack spacing={5} sx={{ pt: 2 }}>
              {Array.from(Array(3).keys()).map(index => (
                <ReplyBoxSkeleton key={index} divider />
              ))}
            </Stack>
          }
        />
        {hiddenReadMore && (
          <>
            <Button
              onClick={() => {
                scrollBottomRef?.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'end',
                  inline: 'nearest',
                })
                onHiddenReadMore && onHiddenReadMore()
              }}
              startIcon={
                <Stack alignItems="center">
                  <Icons.DownArrow
                    fontSize="xSmall"
                    sx={{ color: 'text.primary' }}
                  />
                </Stack>
              }
              sx={{
                position: 'absolute',
                bottom: 12,
                zIndex: 3,
                mb: styles.safeAreaBottom,
                width: 'fit-content',
                py: 1,
                bgcolor: 'grey.100',
                left: '50%',
                transform: 'translate(-50%, 0)',
                '&:hover': { bgcolor: 'grey.200' },
              }}
            >
              <Typography variant="subtitle2">{t('unreadComments')}</Typography>
            </Button>
            <div ref={scrollBottomRef} />
          </>
        )}
      </Box>
    )
  }
)

const NoticeTextCell = () => {
  const { t } = useTranslation()
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Stack direction="row">
        <Typography variant="caption" color="text.secondary">
          <BrowserLink variant="caption" href={getHelpUrl('thread')}>
            {t('noticePrefix')}
          </BrowserLink>
          {t('noticeSuffix')}
        </Typography>
      </Stack>
    </CellFrame>
  )
}
